'use client';
import { useEffect, useMemo, useState } from 'react';
import { debounce } from 'es-toolkit';
import { usePreservedCallback } from './usePreservedCallback';

type UseDebouncedNumberCommitParams = {
  initialValue: number;
  delayMs?: number;
  onCommit: (value: number) => void;
  min?: number;
  max?: number;
};

type UseDebouncedNumberCommitReturn = {
  value: number;
  setValueDebounced: (nextValue: number) => void;
  setValueImmediate: (nextValue: number) => void;
  updateBy: (delta: number) => void;
  cancel: () => void;
};

export function useDebouncedNumberCommit({
  initialValue,
  delayMs = 300,
  onCommit,
  min = 0,
  max = 9999,
}: UseDebouncedNumberCommitParams): UseDebouncedNumberCommitReturn {
  const [value, setValue] = useState<number>(initialValue);
  const handleCommit = usePreservedCallback(onCommit);
  const clamp = (n: number) => {
    let next = n;
    next = Math.max(min, next);
    next = Math.min(max, next);
    return next;
  };

  const debouncedCommit = useMemo(
    () =>
      debounce(
        (next: number) => {
          handleCommit(next);
        },
        delayMs,
        { edges: ['trailing'] },
      ),
    [handleCommit, delayMs],
  );

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const setValueDebounced = (nextValue: number) => {
    const clamped = clamp(nextValue);
    setValue(clamped);
    debouncedCommit(clamped);
  };

  const setValueImmediate = (nextValue: number) => {
    setValue(clamp(nextValue));
  };

  const cancel = () => debouncedCommit.cancel();

  const updateBy = (delta: number) => {
    setValue((prev) => {
      const next = clamp(prev + delta);
      debouncedCommit(next);
      return next;
    });
  };

  return { value, setValueDebounced, setValueImmediate, updateBy, cancel };
}
